import { useCallback, useEffect, useState } from 'react';

import { Close, Email, Person, Send } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { ConfirmDialog } from 'src/components/ConfimDialog';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  cancelInvitation,
  getInvitedUsers,
  inviteMonitorUser,
  resendInvitation,
  WalletInvitation,
  WalletInvitationStatus,
} from 'src/services/invite-monitor';
import { setBannerMessage } from 'src/store/display';
import { isValidEmail } from 'src/utils/validations';

type InvitedUserDisplay = {
  id: number;
  name: string;
  email: string;
  status: WalletInvitationStatus;
};

type InvitesModalProps = {
  open: boolean;
  onClose: () => void;
};

const DEFAULT_ROWS_PER_PAGE = 10;

const mapInvitationToDisplay = (
  invitation: WalletInvitation,
): InvitedUserDisplay => {
  return {
    id: invitation.id,
    email: invitation.email,
    name: invitation.name || '',
    status: invitation.status,
  };
};

const getStatusLabel = (status: WalletInvitationStatus): string => {
  const statusMap: Record<WalletInvitationStatus, string> = {
    pending: 'Pendente',
    accepted: 'Aceito',
    rejected: 'Rejeitado',
    canceled: 'Cancelado',
    expired: 'Expirado',
  };
  return statusMap[status];
};

const getStatusColor = (
  status: WalletInvitationStatus,
): { backgroundColor: string; color: string } => {
  switch (status) {
    case 'accepted':
      return {
        backgroundColor: '#4CAF50',
        color: '#FFFFFF',
      };
    case 'pending':
      return {
        backgroundColor: '#FF9800',
        color: '#FFFFFF',
      };
    case 'rejected':
      return {
        backgroundColor: '#F44336',
        color: '#FFFFFF',
      };
    case 'canceled':
      return {
        backgroundColor: '#9E9E9E',
        color: '#FFFFFF',
      };
    case 'expired':
      return {
        backgroundColor: '#757575',
        color: '#FFFFFF',
      };
    default:
      return {
        backgroundColor: '#E0E0E0',
        color: '#616161',
      };
  }
};

export function InvitesModal({ open, onClose }: InvitesModalProps) {
  const { auth } = useAuthContext();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [invitationToRemove, setInvitationToRemove] =
    useState<InvitedUserDisplay | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const dispatch = useDispatch();

  // Chave do localStorage baseada no ID do usuário para evitar conflitos
  const getStorageKey = useCallback(() => {
    return `invite-resend-timestamps-${auth?.user?.id || 'anonymous'}`;
  }, [auth?.user?.id]);

  // Função para carregar timestamps do localStorage
  const loadTimestampsFromStorage = useCallback((): Record<number, number> => {
    try {
      const storageKey = getStorageKey();
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        // Filtra apenas timestamps que ainda estão dentro do período de cooldown (5 minutos)
        const validTimestamps: Record<number, number> = {};
        Object.entries(parsed).forEach(([id, timestamp]) => {
          const invitationId = Number(id);
          const timestampValue = Number(timestamp);
          const elapsed = now - timestampValue;
          // Mantém apenas se ainda estiver em cooldown (menos de 5 minutos)
          if (elapsed < 5 * 60 * 1000) {
            validTimestamps[invitationId] = timestampValue;
          }
        });
        return validTimestamps;
      }
    } catch (error) {
      // Silenciosamente ignora erros de parsing do localStorage
      // Retorna objeto vazio para evitar quebras
    }
    return {};
  }, [getStorageKey]);

  // Função para salvar timestamps no localStorage
  const saveTimestampsToStorage = useCallback(
    (timestamps: Record<number, number>) => {
      try {
        const storageKey = getStorageKey();
        localStorage.setItem(storageKey, JSON.stringify(timestamps));
      } catch (error) {
        // Silenciosamente ignora erros de salvamento no localStorage
        // (ex: quota excedida, storage desabilitado)
      }
    },
    [getStorageKey],
  );

  const [resendTimestamps, setResendTimestamps] = useState<
    Record<number, number>
  >({});

  const {
    data: invitations = [],
    isLoading: isLoadingInvitedUsers,
    refetch: refetchInvitations,
  } = useQuery({
    queryKey: ['wallet-invitations', auth?.user?.id],
    queryFn: () => getInvitedUsers(auth?.user?.id),
    enabled: !!auth?.user?.id && open,
    refetchOnWindowFocus: false,
  });

  // Carrega timestamps do localStorage quando o componente monta ou quando o usuário muda
  useEffect(() => {
    if (!auth?.user?.id || !open) {
      return;
    }
    const loadedTimestamps = loadTimestampsFromStorage();
    if (Object.keys(loadedTimestamps).length > 0) {
      setResendTimestamps(loadedTimestamps);
    }
  }, [auth?.user?.id, open, loadTimestampsFromStorage]);

  // Limpa timestamps expirados e atualiza o tempo atual
  useEffect(() => {
    const now = Date.now();
    const expiredIds: number[] = [];
    const activeTimestamps: Record<number, number> = {};

    // Separa timestamps ativos dos expirados
    Object.entries(resendTimestamps).forEach(([id, timestamp]) => {
      const invitationId = Number(id);
      const elapsed = now - timestamp;
      if (elapsed >= 5 * 60 * 1000) {
        // Expirado - marca para remoção
        expiredIds.push(invitationId);
      } else {
        // Ainda ativo - mantém
        activeTimestamps[invitationId] = timestamp;
      }
    });

    // Se houver timestamps expirados, remove-os do estado e do localStorage
    if (expiredIds.length > 0) {
      setResendTimestamps(activeTimestamps);
      saveTimestampsToStorage(activeTimestamps);
    }

    const hasActiveCooldowns = Object.keys(activeTimestamps).length > 0;

    if (!hasActiveCooldowns) {
      return;
    }

    // Atualiza o tempo atual a cada segundo para atualizar os contadores
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimestamps, saveTimestampsToStorage]);

  const { mutate: inviteUser } = useMutation(
    ({ name, email }: { name: string; email: string }) =>
      inviteMonitorUser(name, email),
    {
      onSuccess: () => {
        refetchInvitations();
        dispatch(
          setBannerMessage({
            message: 'Convite enviado com sucesso!',
            type: 'success',
            autoclose: true,
          }),
        );
        setEmail('');
        setEmailError('');
        setEmailTouched(false);
        setName('');
        setNameError('');
        setNameTouched(false);
      },
      onError: (error: any) => {
        if (error?.response?.data?.error?.name === 'ForbiddenError') {
          setEmailError(
            error?.response?.data?.error?.details?.email ||
              error?.response?.data?.error?.message,
          );
        } else {
          dispatch(
            setBannerMessage({
              message: 'Erro ao convidar usuário',
              type: 'error',
              autoclose: true,
            }),
          );
        }
      },
    },
  );

  const { mutate: cancelInvitationMutation, isLoading: isCanceling } =
    useMutation((id: number) => cancelInvitation(id), {
      onSuccess: () => {
        dispatch(
          setBannerMessage({
            message: 'Compartilhamento cancelado com sucesso!',
            type: 'success',
            autoclose: true,
          }),
        );
        refetchInvitations();
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.message ||
          'Erro ao cancelar compartilhamento';
        dispatch(
          setBannerMessage({
            message: errorMessage,
            type: 'error',
            autoclose: true,
          }),
        );
      },
    });

  const invitedUsers: InvitedUserDisplay[] = invitations.map(
    mapInvitationToDisplay,
  );

  useEffect(() => {
    if (!open) {
      setEmail('');
      setEmailError('');
      setEmailTouched(false);
      setName('');
      setNameError('');
      setNameTouched(false);
      setOpenConfirmDialog(false);
      setInvitationToRemove(null);
    }
  }, [open]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setName(value);
  };

  const handleNameBlur = () => {
    setNameTouched(true);
    validateNameField(name);
  };

  const validateNameField = (nameValue: string) => {
    const trimmedName = nameValue.trim();
    if (!trimmedName) {
      setNameError('Campo obrigatório');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);

    if (emailTouched) {
      validateEmailField(value);
    }
  };

  const validateEmailField = (emailValue: string) => {
    const trimmedEmail = emailValue.trim();
    if (!trimmedEmail) {
      setEmailError('Campo obrigatório');
      return false;
    }
    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Digite um e-mail válido');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    validateEmailField(email);
  };

  const handleInvite = () => {
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();

    setEmailTouched(true);
    setNameTouched(true);

    if (!validateEmailField(trimmedEmail) || !validateNameField(trimmedName)) {
      return;
    }

    inviteUser({ name: trimmedName, email: trimmedEmail });
  };

  const handleRemove = (user: InvitedUserDisplay) => {
    setInvitationToRemove(user);
    setOpenConfirmDialog(true);
  };

  const handleResendInvite = async (user: InvitedUserDisplay) => {
    const timestamp = Date.now();
    const newTimestamps = {
      ...resendTimestamps,
      [user.id]: timestamp,
    };

    // Atualiza o estado e salva no localStorage
    setResendTimestamps(newTimestamps);
    saveTimestampsToStorage(newTimestamps);

    const result = await resendInvitation(user.email);

    if (result?.data?.message) {
      dispatch(
        setBannerMessage({
          message: result?.data?.message || 'Convite reenviado com sucesso!',
          type: 'success',
          autoclose: true,
        }),
      );
    } else {
      dispatch(
        setBannerMessage({
          message: result?.error?.message || 'Erro ao reenviar convite',
          type: 'error',
          autoclose: true,
        }),
      );
    }
  };

  // Verifica se um convite pode ser reenviado (não está em cooldown)
  const canResendInvite = (invitationId: number): boolean => {
    const timestamp = resendTimestamps[invitationId];
    if (!timestamp) {
      return true;
    }
    const elapsed = currentTime - timestamp;
    return elapsed >= 5 * 60 * 1000; // 5 minutos em milissegundos
  };

  // Calcula o tempo restante em segundos
  const getRemainingTime = (invitationId: number): number => {
    const timestamp = resendTimestamps[invitationId];
    if (!timestamp) {
      return 0;
    }
    const elapsed = currentTime - timestamp;
    const remaining = 5 * 60 * 1000 - elapsed; // 5 minutos em milissegundos
    return Math.max(0, Math.ceil(remaining / 1000)); // Retorna em segundos
  };

  // Formata o tempo restante em minutos:segundos
  const formatRemainingTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleConfirmRemove = () => {
    if (invitationToRemove) {
      cancelInvitationMutation(invitationToRemove.id);
      setOpenConfirmDialog(false);
      setInvitationToRemove(null);
    }
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = invitedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const trimmedEmail = email.trim();
  const isEmailValid =
    trimmedEmail.length > 0 && isValidEmail(trimmedEmail) && !emailError;

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0 && !nameError;

  const isLoading = isLoadingInvitedUsers;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          paddingBottom: 1,
        }}>
        <Typography
          variant="h5"
          align="center"
          sx={{
            fontSize: '24px',
            fontWeight: 700,
            marginTop: 1,
            marginBottom: 4,
          }}>
          Compartilhe seu ambiente
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '0 48px 24px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}>
          {/* Description */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#1D1D1D',
                textAlign: 'center',
              }}>
              Convide outros usuários para que eles possam criar/editar fundos
              de carteiras de seu ambiente monitorado
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                lineHeight: 1.5,
                color: '#1D1D1D',
                textAlign: 'center',
              }}>
              <strong>Atenção:</strong> O convidado terá acesso a suas carteiras
              até que você remova suas permissões.
            </Typography>
          </Box>

          {/* Invite Input Section */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}>
            <TextField
              fullWidth
              placeholder="Digite o nome do usuário que deseja convidar"
              value={name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              error={nameTouched && !!nameError}
              helperText={nameTouched && nameError ? nameError : ''}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: '#828282' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              placeholder="Digite o e-mail do usuário que deseja convidar"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              error={emailTouched && !!emailError}
              helperText={emailTouched && emailError ? emailError : ''}
              onKeyPress={(event) => {
                if (event.key === 'Enter' && isEmailValid) {
                  handleInvite();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#828282' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleInvite}
              disabled={!isEmailValid || !isNameValid}
              sx={{
                minWidth: 120,
                height: '40px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                '&:disabled': {
                  backgroundColor: '#E0E0E0',
                  color: '#9E9E9E',
                },
              }}>
              Compartilhar
            </Button>
          </Box>

          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              {/* Invited Users Table */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: '#1D1D1D',
                          }}>
                          Nome
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: '#1D1D1D',
                          }}>
                          Email
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: '#1D1D1D',
                          }}>
                          Status
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: '#1D1D1D',
                          }}>
                          Ações
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedUsers.length > 0 ? (
                        paginatedUsers.map((user: InvitedUserDisplay) => (
                          <TableRow key={user.id} hover>
                            <TableCell
                              sx={{
                                fontSize: '14px',
                                color: '#1D1D1D',
                              }}>
                              {user.name}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: '14px',
                                color: '#1D1D1D',
                              }}>
                              {user.email}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusLabel(user.status)}
                                size="small"
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  height: '24px',
                                  ...getStatusColor(user.status),
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  justifyContent: 'flex-end',
                                }}>
                                {user.status === 'pending' && (
                                  <Button
                                    variant="text"
                                    startIcon={<Send fontSize="small" />}
                                    aria-label="Reenviar Convite"
                                    onClick={() => handleResendInvite(user)}
                                    disabled={!canResendInvite(user.id)}
                                    size="small"
                                    sx={{
                                      color: '#1976D2',
                                      '&:hover:not(:disabled)': {
                                        backgroundColor: '#E3F2FD',
                                      },
                                      '&:disabled': {
                                        color: '#9E9E9E',
                                      },
                                    }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        color: canResendInvite(user.id)
                                          ? '#1976D2'
                                          : '#9E9E9E',
                                        textTransform: 'capitalize',
                                      }}>
                                      {canResendInvite(user.id)
                                        ? 'Reenviar Convite'
                                        : `Aguarde ${formatRemainingTime(
                                            getRemainingTime(user.id),
                                          )}`}
                                    </Typography>
                                  </Button>
                                )}
                                {user.status !== 'rejected' &&
                                  user.status !== 'canceled' && (
                                    <IconButton
                                      aria-label="Cancelar Compartilhamento"
                                      onClick={() => handleRemove(user)}
                                      size="small"
                                      sx={{
                                        color: '#F44336',
                                        '&:hover': {
                                          backgroundColor: '#FFEBEE',
                                        },
                                      }}>
                                      <Close fontSize="small" />
                                    </IconButton>
                                  )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{
                              padding: 4,
                              border: 'none',
                            }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '14px',
                                color: '#616161',
                              }}>
                              Não há usuários convidados para serem exibidos
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    {invitedUsers.length > 0 && (
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            count={invitedUsers.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            labelRowsPerPage="Linhas por página:"
                            labelDisplayedRows={({ from, to, count }) =>
                              `${from}-${to} de ${count}`
                            }
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                          />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setInvitationToRemove(null);
        }}
        submitAction={handleConfirmRemove}
        hasActionButton={true}
        title="Cancelar compartilhamento"
        subTitle={
          invitationToRemove
            ? `Tem certeza que deseja cancelar o compartilhamento do ambiente com ${invitationToRemove.name || invitationToRemove.email}?`
            : 'Tem certeza que deseja cancelar este compartilhamento?'
        }
        submitButtonColor="inherit"
        submitButtonTextVariant="outlined"
        cancelButtonColor="primary"
        cancelButtonTextVariant="contained"
      />
    </Dialog>
  );
}
